// apps/frontend/src/services/pushService.ts
// Web Push subscription management

import { API_BASE_URL } from '../config/api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function getDeviceInfo(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iOS Safari';
  if (/Android/.test(ua)) return 'Android Chrome';
  if (/Windows/.test(ua)) return 'Windows Desktop';
  if (/Mac/.test(ua)) return 'Mac Desktop';
  return 'Unknown';
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

export async function isPushSubscribed(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

export function isPushDenied(): boolean {
  return 'Notification' in window && Notification.permission === 'denied';
}

export function isPushPromptDismissed(): boolean {
  const dismissed = localStorage.getItem('glowp_push_dismissed');
  if (!dismissed) return false;
  const ts = parseInt(dismissed, 10);
  // Re-show after 30 days
  return Date.now() - ts < 30 * 24 * 60 * 60 * 1000;
}

export function dismissPushPrompt(): void {
  localStorage.setItem('glowp_push_dismissed', String(Date.now()));
}

/**
 * Request push permission and subscribe the browser.
 * Returns true if subscription succeeded, false otherwise.
 */
export async function subscribeToPush(token: string): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    // 1. Request permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;

    // 2. Get VAPID public key from backend
    const keyRes = await fetch(
      `${API_BASE_URL}/api/v1/notifications/push/vapid-key`
    );
    const keyData = await keyRes.json();
    if (!keyData.success || !keyData.data?.publicKey) return false;

    // 3. Subscribe via Push API
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        keyData.data.publicKey
      ) as BufferSource,
    });

    // 4. Send subscription to backend
    const sub = subscription.toJSON();
    const res = await fetch(
      `${API_BASE_URL}/api/v1/notifications/push/subscribe`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          endpoint: sub.endpoint,
          keys: sub.keys,
          deviceInfo: getDeviceInfo(),
        }),
      }
    );

    return res.ok;
  } catch (err) {
    console.error('[Push] Subscribe failed:', err);
    return false;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush(token: string): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const reg = await navigator.serviceWorker.ready;
    const subscription = await reg.pushManager.getSubscription();
    if (!subscription) return true;

    const endpoint = subscription.endpoint;
    await subscription.unsubscribe();

    await fetch(`${API_BASE_URL}/api/v1/notifications/push/unsubscribe`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ endpoint }),
    });

    return true;
  } catch (err) {
    console.error('[Push] Unsubscribe failed:', err);
    return false;
  }
}
